import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { Operator } from '@procureai/query-builder';
import { DateFormatPreset, NumberFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { CommodityGroupFilterField } from '@/generated/gql/graphql';
import { BaseDomainType, OrderDataGridColumnGroup, OrderDataGridField, OrderDataGridRow } from '~/common/types';
import { CuriosityListCountCellMenu } from '~/components/CuriosityListCountCellMenu/CuriosityListCountCellMenu';
import {
  cellFactory,
  ChipCell,
  CurrencyCell,
  DataGridConfig,
  DataGridLinkVariant,
  dateFormatterFactory,
  GridAlignmentType,
  gridColDefFactory,
  gridColumnGroupFactory,
  GridSortDirectionType,
  headerGroupWithIcon,
  LinkCell,
  LinkCellValue,
  ListCountCell,
  numberFormatterFactory,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { commodityGroupValueGetter } from '~/components/DataGridWidget/DataGridWidget.util';
import { IconId } from '~/components/Icon';
import { ListCountCellMenu } from '~/components/ListCountCellMenu/ListCountCellMenu';
import { LocationLink } from '~/components/LocationLink/LocationLink';
import { SourceLinksButton } from '~/components/SourceLinksButton/SourceLinksButton';
import { RouteId } from '~/routes';
import { notEmpty } from '~/util/array';

export const useOrderDataGridConfig: UseDataGridConfig<OrderDataGridRow> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(undefined, { keyPrefix: 'Base.order.columns' });
  const { t: tGroup } = useTranslation(undefined, { keyPrefix: 'Base.order.columnBands' });

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });
    const createGroup = gridColumnGroupFactory({ tGroup });
    return {
      sortModel: [{ field: OrderDataGridField.ORDER_CREATION_DATE, sort: GridSortDirectionType.DESC }],
      columnVisibilityModel: {
        [OrderDataGridField.SUPPLIER_EXTERNAL_ID]: false,
        [OrderDataGridField.SUPPLIER_EMPLOYEE_COUNT]: false,
        [OrderDataGridField.SUPPLIER_INDUSTRY]: false,
        [OrderDataGridField.SUPPLIER_LEGAL_TYPE]: false,
        [OrderDataGridField.SUPPLIER_ADDRESS]: false,
        [OrderDataGridField.REPRESENTATIVE_COUNT]: false,
        [OrderDataGridField.CONTRACT_VALID_FROM]: false,
        [OrderDataGridField.CONTRACT_VALID_TO]: false,
        [OrderDataGridField.CONTRACT_RECEIVED_PROPOSALS_COUNT]: false,
        [OrderDataGridField.CONTRACT_AVERAGE_VALUE_CONTRIBUTION]: false,
        [OrderDataGridField.ORDER_PRODUCT_COUNT]: false,
        [OrderDataGridField.ORDER_PRODUCT_CATEGORY_COUNT]: false,
        [OrderDataGridField.ORDER_SERVICE_COUNT]: false,
      },
      columns: [
        createColumn({
          field: OrderDataGridField.EXTERNAL_ID,
          renderCell: cellFactory(LinkCell),
          valueGetter: (value, row): LinkCellValue => ({
            variant: DataGridLinkVariant.PRIMARY,
            to: RouteId.orderDetail(row.id),
            label: value,
          }),
          valueFormatter: (value: { label: string }) => value?.label,
          minWidth: 200,
          hideable: false,
        }),
        createColumn({
          field: OrderDataGridField.ORDER_CREATION_DATE,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          minWidth: 140,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: OrderDataGridField.ORDER_VOLUME,
          renderCell: cellFactory(CurrencyCell),
          minWidth: 200,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: OrderDataGridField.SUPPLIER_NAME,
          renderCell: cellFactory(LinkCell, { needsPermission: Permission.BaseSupplierList }),
          valueGetter: (value, row): LinkCellValue => ({
            to: row.supplierId ? RouteId.supplierDetail(row.supplierId) : '',
            label: value,
          }),
          valueFormatter: (value: { label: string }) => value?.label,
          minWidth: 350,
        }),
        createColumn({
          field: OrderDataGridField.SUPPLIER_EXTERNAL_ID,
          minWidth: 120,
        }),
        createColumn({
          field: OrderDataGridField.SUPPLIER_EMPLOYEE_COUNT,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: 120,
        }),
        createColumn({
          field: OrderDataGridField.SUPPLIER_INDUSTRY,
          valueGetter: (value: OrderDataGridRow['supplierIndustry']) => value?.name,
          minWidth: 150,
        }),
        createColumn({
          field: OrderDataGridField.SUPPLIER_LEGAL_TYPE,
          valueGetter: (value: OrderDataGridRow['supplierLegalType']) => value?.name,
          minWidth: 150,
        }),
        createColumn({
          field: OrderDataGridField.SUPPLIER_ADDRESS,
          renderCell: cellFactory(LocationLink),
          minWidth: 600,
          sortable: false,
        }),
        createColumn({
          field: OrderDataGridField.REPRESENTATIVE_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList: notEmpty(row.representativeList) && (() => <ListCountCellMenu list={row.representativeList} />),
          })),
          minWidth: 140,
        }),
        createColumn({
          field: OrderDataGridField.CONTRACT_CREATION_DATE,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          minWidth: 140,
        }),
        createColumn({
          field: OrderDataGridField.CONTRACT_VALID_FROM,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          minWidth: 140,
        }),
        createColumn({
          field: OrderDataGridField.CONTRACT_VALID_TO,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          minWidth: 140,
        }),
        createColumn({
          field: OrderDataGridField.CONTRACT_RECEIVED_PROPOSALS_COUNT,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: 170,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: OrderDataGridField.CONTRACT_AVERAGE_VALUE_CONTRIBUTION,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: OrderDataGridField.ORDER_PRODUCT_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList: notEmpty(row.orderProductList) && (() => <ListCountCellMenu list={row.orderProductList} />),
          })),
          minWidth: 180,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: OrderDataGridField.ORDER_PRODUCT_CATEGORY_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList:
              notEmpty(row.orderProductCategoryList) &&
              (() => <ListCountCellMenu list={row.orderProductCategoryList} />),
          })),
          minWidth: 180,
        }),
        createColumn({
          field: OrderDataGridField.ORDER_SERVICE_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList: notEmpty(row.orderServiceList) && (() => <ListCountCellMenu list={row.orderServiceList} />),
          })),
          minWidth: 180,
        }),
        createColumn({
          field: OrderDataGridField.PRIMARY_COMMODITY_GROUP,
          renderCell: cellFactory(ChipCell, ({ value }) => ({ value: commodityGroupValueGetter(value) })),
          minWidth: 120,
          sortable: false,
        }),
        createColumn({
          field: OrderDataGridField.COMMODITY_GROUP_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList:
              notEmpty(row.commodityGroupList) &&
              (() => (
                <CuriosityListCountCellMenu
                  ns={CuriosityNamespaceType.COMMODITY_GROUP}
                  list={row.commodityGroupList}
                  link={{
                    needs: { permission: Permission.BaseCommodityGroupList },
                    all: {
                      label: t('Base.order.menu.commodityGroupCount.all'),
                      pathname: RouteId.analyticsBase(BaseDomainType.COMMODITY_GROUP),
                      rules: [
                        {
                          field: CommodityGroupFilterField.id,
                          operator: Operator.INEQ,
                          value: row.commodityGroupList.map(({ id }) => id),
                        },
                      ],
                    },
                    to: (id) => RouteId.commodityGroupDetail(id),
                    component: Link,
                  }}
                />
              )),
          })),
          minWidth: 100,
        }),
        createColumn({
          field: OrderDataGridField.LINKS,
          renderCell: cellFactory(SourceLinksButton, ({ value }) => ({ links: value })),
          minWidth: 120,
          sortable: false,
        }),
      ],
      columnGroupingModel: [
        createGroup({
          groupId: OrderDataGridColumnGroup.ORDER,
          renderHeaderGroup: headerGroupWithIcon(IconId.order),
          children: [
            //
            { field: OrderDataGridField.EXTERNAL_ID },
            { field: OrderDataGridField.ORDER_CREATION_DATE },
            { field: OrderDataGridField.ORDER_VOLUME },
            { field: OrderDataGridField.PRIMARY_COMMODITY_GROUP },
            { field: OrderDataGridField.COMMODITY_GROUP_COUNT },
            { field: OrderDataGridField.LINKS },
          ],
        }),
        createGroup({
          groupId: OrderDataGridColumnGroup.SUPPLIER,
          renderHeaderGroup: headerGroupWithIcon(IconId.supplier),
          children: [
            //
            { field: OrderDataGridField.SUPPLIER_NAME },
            { field: OrderDataGridField.SUPPLIER_EXTERNAL_ID },
            { field: OrderDataGridField.SUPPLIER_EMPLOYEE_COUNT },
            { field: OrderDataGridField.SUPPLIER_INDUSTRY },
            { field: OrderDataGridField.SUPPLIER_LEGAL_TYPE },
            { field: OrderDataGridField.SUPPLIER_ADDRESS },
            { field: OrderDataGridField.REPRESENTATIVE_COUNT },
          ],
        }),
        createGroup({
          groupId: OrderDataGridColumnGroup.CONTRACT,
          renderHeaderGroup: headerGroupWithIcon(IconId.contract),
          children: [
            //
            { field: OrderDataGridField.CONTRACT_CREATION_DATE },
            { field: OrderDataGridField.CONTRACT_VALID_FROM },
            { field: OrderDataGridField.CONTRACT_VALID_TO },
            { field: OrderDataGridField.CONTRACT_RECEIVED_PROPOSALS_COUNT },
            { field: OrderDataGridField.CONTRACT_AVERAGE_VALUE_CONTRIBUTION },
          ],
        }),
        createGroup({
          groupId: OrderDataGridColumnGroup.PRODUCT,
          renderHeaderGroup: headerGroupWithIcon(IconId.Widgets),
          children: [
            //
            { field: OrderDataGridField.ORDER_PRODUCT_COUNT },
            { field: OrderDataGridField.ORDER_PRODUCT_CATEGORY_COUNT },
          ],
        }),
        createGroup({
          groupId: OrderDataGridColumnGroup.SERVICE,
          renderHeaderGroup: headerGroupWithIcon(IconId.Person),
          children: [
            //
            { field: OrderDataGridField.ORDER_SERVICE_COUNT },
          ],
        }),
      ],
      exportModel: {
        [OrderDataGridField.CONTRACT_AVERAGE_VALUE_CONTRIBUTION]: {
          numFmt: '0.00%',
        },
        [OrderDataGridField.LINKS]: {
          disable: true,
        },
      },
    } as DataGridConfig<OrderDataGridRow>;
  }, [t, tColumn, tGroup]);
};
